import { MARKET_CONTENT } from "../../src/i18n/market-content";
import React from "react";
import { createRoot } from "react-dom/client";
import { LineButton } from "./contact";
import { ChunkErrorBoundary, HelpCenter, HelpPublicHeader, LoadingView, PublicFooter, PublicSalesLayout, loadTourSession, marketingHost } from "./app-core";
import { AboutPage, ContactPage, EmailAuthPage, Landing, PublicPage, TourPage } from "./public-pages";
import { isArticlesPrefixPath, isMarketingPrefixPath, isToolsPrefixPath, marketingPageMetadataFor } from "../../src/marketing-pages";
// 教學專欄 (D-270): not lazy, because the page is already on screen as server HTML and this only wraps it.
import ArticlesRoute from "./articles-page";
import { basePath, isDefaultLocale, loadMessages, locale, siteName, t } from "./i18n";
import { updateBlocked } from "./new-version";
import { bootAppearance } from "./appearance";
// The teacher workspace is its own chunk so public pages never download it.
// After a deploy, old hashed chunks are gone: reload once to pick up the new build.
window.addEventListener("vite:preloadError", (event) => {
  // Never reload under a lesson in progress (a score still saving, a dialog, the projection): the page that failed shows its own notice instead.
  if (updateBlocked()) return;
  // Inside the teacher workspace a lazily loaded page that fails shows its own notice with a refresh button; reloading under the teacher is for the app shell only.
  if (document.querySelector(".app")) return;
  try {
    const last = Number(sessionStorage.getItem("classmori-chunk-reload") ?? 0);
    if (Date.now() - last < 60_000) return;
    sessionStorage.setItem("classmori-chunk-reload", String(Date.now()));
  } catch { return; }
  event.preventDefault();
  location.reload();
});
const App = React.lazy(() => import("./workspace").then((module) => ({ default: module.App })));
// Feature introductions and articles are their own chunk (copy and styles stay off the homepage).
const MarketingRoute = React.lazy(() => import("./marketing-pages"));
const UpdatesPage = React.lazy(() => import("./updates-page"));
// Free no-login tools (/tools): their own small chunk; never the teacher workspace.
const PublicToolsRoute = React.lazy(() => import("./public-tools"));
// A child's private link (/s/<token>, D-125): its own tiny page, never the workspace or the public layout.
const StudentView = React.lazy(() => import("./student-view"));
const studentViewToken = /^\/s\/([A-Za-z0-9_-]{22})\/?$/.exec(location.pathname)?.[1] ?? null;

// Routes are matched on the path without its locale prefix (/en/pricing → /pricing).
const publicKind = basePath.slice(1) as
  "about" | "tour" | "pricing" | "privacy" | "terms" | "contact" | "updates";
const publicRoute = ["pricing", "privacy", "terms"].includes(publicKind);
const aboutRoute = publicKind === "about";
const contactRoute = publicKind === "contact";
const updatesRoute = publicKind === "updates";
const helpRoute = basePath === "/help" || basePath.startsWith("/help/");
const articlesRoute = isArticlesPrefixPath(basePath);
const marketingRoute = isMarketingPrefixPath(basePath) && !articlesRoute;
const toolsRoute = isToolsPrefixPath(basePath);
const tourRouteActive = publicKind === "tour";
// /tour needs the workspace chunk and the in-memory adapter; fetch both alongside the
// demo snapshot instead of after it (they used to wait for the snapshot to arrive).
if (tourRouteActive) { void import("./workspace").catch(() => {}); void loadTourSession().catch(() => {}); }
const authRoute = ["login", "register", "verify-email", "forgot-password", "reset-password"].includes(publicKind);
// LINE is a Taiwan channel; other languages use the contact page and email.
const showPublicLineButton = marketingHost && isDefaultLocale && ["/", "/pricing", "/about"].includes(basePath);

// Home page title per language (the Worker injects the same text server-side, src/i18n/page-metadata.ts).
const HOME_TITLES: Partial<Record<typeof locale, string>> = {
  "pt-BR": MARKET_CONTENT["pt-BR"].corePages["/"].title,
  "tr": MARKET_CONTENT["tr"].corePages["/"].title,
  "vi": MARKET_CONTENT["vi"].corePages["/"].title,
  "id": MARKET_CONTENT["id"].corePages["/"].title,

  "zh-Hans": "小森课堂｜上课点一下加分，孩子的宠物当场长大", // i18n-ignore
  "zh-Hant": "小森課堂｜上課點一下加分，孩子的寵物當場長大", // i18n-ignore
  en: "ClassMori | Classroom points that grow a pet for every student",
  es: "ClassMori | Puntos en el aula que hacen crecer una mascota",
  fr: "ClassMori | Des points en classe qui font grandir un animal",
};

function pageTitle() {
  const titled = (name: string) => isDefaultLocale ? `${name}｜${siteName}` : `${name} | ${siteName}`;
  const pageTitles: Record<string, string> = { updates: t("最近更新"), about: t("關於我們"), contact: t("聯絡我們"), tour: t("後台導覽"), pricing: t("方案與價格"), privacy: t("隱私說明"), terms: t("使用條款"), login: t("教師登入"), register: t("建立教師帳號"), "forgot-password": t("找回密碼"), "reset-password": t("設定新密碼"), "verify-email": t("驗證電子郵件") };
  if (helpRoute) return titled(t("幫助中心"));
  // The Worker wrote the article's (or the 404's) title; the app has no article data to rebuild it from.
  if (articlesRoute && document.title) return document.title;
  // The Worker already wrote the search title of /updates (src/updates/page-meta.ts); keep it.
  if (updatesRoute && document.title) return document.title;
  if (marketingRoute) return marketingPageMetadataFor(locale)[basePath]?.title ?? titled(t("找不到頁面"));
  if (pageTitles[publicKind]) return titled(pageTitles[publicKind]);
  if (location.hostname === "app.classmori.com") return titled(t("教師工作空間"));
  return HOME_TITLES[locale] ?? HOME_TITLES.en!;
}

// The teacher workspace (not public pages, help, tools or a child's link) follows the 隨系統／黑夜／白天 choice (D-163).
const workspaceRoute = !studentViewToken && !(helpRoute || articlesRoute || marketingRoute || aboutRoute || updatesRoute || contactRoute || tourRouteActive || publicRoute || authRoute || marketingHost);
if (workspaceRoute) bootAppearance();

function render() {
  document.title = pageTitle();
  createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      {studentViewToken
        ? <ChunkErrorBoundary><React.Suspense fallback={<LoadingView message={t("正在打開你的頁面…")} />}><StudentView token={studentViewToken} /></React.Suspense></ChunkErrorBoundary>
        : helpRoute || articlesRoute || marketingRoute || aboutRoute || updatesRoute || contactRoute || tourRouteActive || publicRoute || authRoute || marketingHost
        ? <PublicSalesLayout>{articlesRoute ? <ArticlesRoute /> : helpRoute ? <ChunkErrorBoundary><React.Suspense fallback={<LoadingView message={t("正在開啟幫助中心…")} />}><HelpCenter header={<HelpPublicHeader />} footer={<PublicFooter />} /></React.Suspense></ChunkErrorBoundary> : toolsRoute ? <ChunkErrorBoundary><React.Suspense fallback={<LoadingView message={t("正在開啟工具…")} />}><PublicToolsRoute path={basePath} /></React.Suspense></ChunkErrorBoundary> : marketingRoute ? <ChunkErrorBoundary><React.Suspense fallback={<LoadingView message={t("正在開啟頁面…")} />}><MarketingRoute path={basePath} /></React.Suspense></ChunkErrorBoundary> : aboutRoute ? <AboutPage /> : updatesRoute ? <ChunkErrorBoundary><React.Suspense fallback={<LoadingView message={t("正在開啟頁面…")} />}><UpdatesPage /></React.Suspense></ChunkErrorBoundary> : contactRoute ? <ContactPage /> : tourRouteActive ? <TourPage /> : publicRoute ? <PublicPage kind={publicKind as "pricing" | "privacy" | "terms"} /> : authRoute ? <EmailAuthPage mode={publicKind as "login" | "register" | "verify-email" | "forgot-password" | "reset-password"} /> : <Landing />}</PublicSalesLayout>
        : <ChunkErrorBoundary><React.Suspense fallback={<LoadingView message={t("正在開啟小森課堂…")} />}><App /></React.Suspense></ChunkErrorBoundary>}
      {showPublicLineButton && <LineButton />}
    </React.StrictMode>,
  );
}

// Non-default languages load their dictionary first (one small chunk); Chinese renders at once.
if (isDefaultLocale) render();
else void loadMessages().then(render, (error) => { console.error("Could not load translations", error); render(); });

if (location.hostname === "classmori.com" && (["/", "/about", "/pricing", "/privacy", "/terms", "/updates"].includes(basePath) || (marketingRoute && marketingPageMetadataFor(locale)[basePath]) || (articlesRoute && document.getElementById("cm-ssr-main"))))
  void import("./vitals").catch((error) => console.warn("Web Vitals measurement unavailable", error));
